import { apiClient } from './apiClient';
import type { 
  User, 
  CreateUserRequest, 
  UpdateUserRequest, 
  ApiResponse, 
  PaginatedResponse,
  UserFilters,
  PaginationParams,
  SystemAnalytics 
} from '../types/api';

class UserService {
  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.get<User>('/auth/me');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update current user profile
   */
  async updateProfile(userData: {
    fullName?: string;
    username?: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.put<User>('/auth/profile', userData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<void>('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get all users (admin/dev only)
   */
  async getAllUsers(filters?: UserFilters, params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<User>>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (filters?.scopes) queryParams.append('scopes', filters.scopes.join(','));
      if (filters?.search) queryParams.append('search', filters.search);
      
      const response = await apiClient.get<PaginatedResponse<User>>(
        `/users?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a specific user by ID (admin/dev only)
   */
  async getUser(userId: string): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.get<User>(`/users/${userId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create a new user (admin only)
   */
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.post<User>('/users', userData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update a user (admin/dev only)
   */
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.put<User>(`/users/${userId}`, userData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete a user (admin only)
   */
  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`/users/${userId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get system analytics (dev only)
   */
  async getSystemAnalytics(): Promise<ApiResponse<SystemAnalytics>> {
    try {
      const response = await apiClient.get<SystemAnalytics>('/analytics/system');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get user activity logs
   */
  async getUserActivityLogs(userId?: string, limit: number = 50): Promise<ApiResponse<Array<{
    timestamp: string;
    action: string;
    details: any;
    ipAddress?: string;
    userAgent?: string;
  }>>> {
    try {
      const queryParams = new URLSearchParams();
      if (userId) queryParams.append('userId', userId);
      queryParams.append('limit', limit.toString());
      
      const response = await apiClient.get<Array<{
        timestamp: string;
        action: string;
        details: any;
        ipAddress?: string;
        userAgent?: string;
      }>>(`/users/activity-logs?${queryParams.toString()}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get user sessions
   */
  async getUserSessions(): Promise<ApiResponse<Array<{
    id: string;
    createdAt: string;
    lastActivity: string;
    ipAddress: string;
    userAgent: string;
    isCurrentSession: boolean;
  }>>> {
    try {
      const response = await apiClient.get<Array<{
        id: string;
        createdAt: string;
        lastActivity: string;
        ipAddress: string;
        userAgent: string;
        isCurrentSession: boolean;
      }>>('/auth/sessions');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Revoke a user session
   */
  async revokeSession(sessionId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`/auth/sessions/${sessionId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Revoke all sessions except current
   */
  async revokeAllOtherSessions(): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<void>('/auth/revoke-all-sessions');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Enable two-factor authentication
   */
  async enableTwoFactor(): Promise<ApiResponse<{ qrCode: string; secret: string }>> {
    try {
      const response = await apiClient.post<{ qrCode: string; secret: string }>('/auth/2fa/enable');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Verify and confirm two-factor authentication
   */
  async confirmTwoFactor(token: string): Promise<ApiResponse<{ backupCodes: string[] }>> {
    try {
      const response = await apiClient.post<{ backupCodes: string[] }>('/auth/2fa/confirm', { token });
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Disable two-factor authentication
   */
  async disableTwoFactor(token: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post<void>('/auth/2fa/disable', { token });
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get user preferences
   */
  async getUserPreferences(): Promise<ApiResponse<{
    notifications: {
      transactions: boolean;
      profiles: boolean;
      system: boolean;
      email: boolean;
      sms: boolean;
    };
    display: {
      theme: 'light' | 'dark' | 'auto';
      language: string;
      timezone: string;
    };
    security: {
      twoFactorEnabled: boolean;
      sessionTimeout: number;
    };
  }>> {
    try {
      const response = await apiClient.get<{
        notifications: {
          transactions: boolean;
          profiles: boolean;
          system: boolean;
          email: boolean;
          sms: boolean;
        };
        display: {
          theme: 'light' | 'dark' | 'auto';
          language: string;
          timezone: string;
        };
        security: {
          twoFactorEnabled: boolean;
          sessionTimeout: number;
        };
      }>('/users/preferences');
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(preferences: {
    notifications?: {
      transactions?: boolean;
      profiles?: boolean;
      system?: boolean;
      email?: boolean;
      sms?: boolean;
    };
    display?: {
      theme?: 'light' | 'dark' | 'auto';
      language?: string;
      timezone?: string;
    };
    security?: {
      sessionTimeout?: number;
    };
  }): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.put<void>('/users/preferences', preferences);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Validate user data before submission
   */
  validateUserData(data: CreateUserRequest | UpdateUserRequest): string[] {
    const errors: string[] = [];

    if ('username' in data && data.username) {
      if (data.username.length < 3) {
        errors.push('Username must be at least 3 characters long');
      }
      if (data.username.length > 50) {
        errors.push('Username cannot exceed 50 characters');
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(data.username)) {
        errors.push('Username can only contain letters, numbers, underscores, and hyphens');
      }
    }

    if ('fullName' in data && data.fullName) {
      if (data.fullName.length < 2) {
        errors.push('Full name must be at least 2 characters long');
      }
      if (data.fullName.length > 100) {
        errors.push('Full name cannot exceed 100 characters');
      }
    }

    if ('password' in data && data.password) {
      if (data.password.length < 6) {
        errors.push('Password must be at least 6 characters long');
      }
      if (data.password.length > 128) {
        errors.push('Password cannot exceed 128 characters');
      }
    }

    if ('scopes' in data && data.scopes) {
      const validScopes = ['user', 'admin', 'dev'];
      const invalidScopes = data.scopes.filter(scope => !validScopes.includes(scope));
      if (invalidScopes.length > 0) {
        errors.push(`Invalid scopes: ${invalidScopes.join(', ')}`);
      }
    }

    return errors;
  }

  /**
   * Get user role display name
   */
  getUserRoleDisplay(scopes: string[]): string {
    if (scopes.includes('dev')) return 'Developer';
    if (scopes.includes('admin')) return 'Administrator';
    if (scopes.includes('user')) return 'User';
    return 'Unknown';
  }

  /**
   * Check if user has specific scope
   */
  hasScope(userScopes: string[], requiredScope: string): boolean {
    return userScopes.includes(requiredScope) || userScopes.includes('admin') || userScopes.includes('dev');
  }

  /**
   * Format user creation date
   */
  formatUserDate(dateString: string): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  }

  /**
   * Get user status color for UI
   */
  getUserStatusColor(_user: User): string {
    // You can extend this based on user status fields
    return 'bg-green-100 text-green-800'; // Default to active
  }

  /**
   * Generate secure password
   */
  generateSecurePassword(length: number = 12): string {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }
}

// Create and export a singleton instance
export const userService = new UserService();
export default userService;
